import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useCart } from "../components/CartContext";
import Reveal from "../components/Reveal";

const CATEGORIES = ["All", "Shirts", "Hoodies", "Hats", "Stickers", "Accessories"];

export default function Merch() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("All");
  const { add, count } = useCart();

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setProducts(r.data))
      .catch(() => toast.error("Couldn't load merch. Try refreshing."));
  }, []);

  const visible =
    filter === "All" ? products : products.filter((p) => p.category === filter);

  const onAdd = (p) => {
    add(p);
    toast.success(`${p.name} added to cart`);
  };

  return (
    <div data-testid="page-merch">
      <section className="relative py-20 border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 moving-gradient pointer-events-none" />
        <div className="container-fld relative z-10">
          <div className="overline mb-4">// Tactical Merch</div>
          <h1 className="font-tactical text-5xl md:text-7xl font-bold uppercase text-white tracking-tight leading-none">
            Wear the <span className="text-[#7CFF00]">Mindset.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-zinc-400 text-lg leading-relaxed">
            Premium tactical apparel and gear, engineered for the prepared.
            Every piece carries the FLD mark.
          </p>
          <div className="mt-8 flex items-center gap-4 flex-wrap">
            <Link to="/cart" data-testid="merch-view-cart" className="btn-secondary">
              <ShoppingBag className="w-4 h-4" />
              View Cart {count > 0 && `(${count})`}
            </Link>
          </div>
        </div>
      </section>

      <section className="container-fld py-12">
        <div className="flex gap-2 flex-wrap mb-10" data-testid="merch-categories">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`merch-cat-${c.toLowerCase()}`}
              onClick={() => setFilter(c)}
              className={`font-tactical uppercase tracking-widest text-xs px-4 py-2 border transition-colors ${
                filter === c
                  ? "border-[#7CFF00] bg-[#7CFF00] text-black"
                  : "border-white/15 text-white/70 hover:border-[#7CFF00] hover:text-[#7CFF00]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          data-testid="merch-grid"
        >
          {visible.map((p, i) => (
            <Reveal key={p.id} delay={i * 60}>
              <div
                data-testid={`product-card-${p.id}`}
                className="tactical-card group relative h-full flex flex-col"
              >
                <div className="aspect-square bg-[#0A0A0A] overflow-hidden relative">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute top-3 left-3 px-2 py-1 bg-black/70 text-[#7CFF00] font-mono-tactical text-[10px] tracking-widest uppercase">
                    {p.category}
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-tactical text-lg uppercase text-white tracking-wider">
                    {p.name}
                  </h3>
                  <p className="text-zinc-500 text-xs mt-1 leading-relaxed flex-1">
                    {p.description}
                  </p>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                    <span className="font-mono-tactical text-[#7CFF00] text-lg">
                      ${p.price.toFixed(2)}
                    </span>
                    <button
                      onClick={() => onAdd(p)}
                      data-testid={`add-to-cart-${p.id}`}
                      className="font-tactical uppercase text-xs tracking-widest text-black bg-[#7CFF00] px-3 py-2 hover:bg-[#65CC00] transition-colors flex items-center gap-1"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {visible.length === 0 && (
          <div className="py-24 text-center text-zinc-500 font-mono-tactical">
            // No products in this category yet
          </div>
        )}
      </section>
    </div>
  );
}
